/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Globe, Loader2 } from 'lucide-react';
import { queryClient } from '@utils/api/react-query-instance.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  useGetResellerLanguagePublications,
  RESELLER_LANGUAGE_PUBLICATIONS_QUERY_KEY,
  type ResellerLanguageOrg,
  type ResellerLanguagePublication
} from '@utils/api/resellers/use-get-reseller-language-publications.ts';
import { useEnableResellerLanguagePublication } from '@utils/api/resellers/use-enable-reseller-language-publication.ts';
import { useDisableResellerLanguagePublication } from '@utils/api/resellers/use-disable-reseller-language-publication.ts';
import Switch from '@components/atoms/ui-kit/switch.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';

type RevokeTarget = {
  languageName: string;
  organizationName: string;
  publicationId: number;
};

function OrgAccessRow({
  language,
  org,
  onEnable,
  onRevokeRequest,
  isPending
}: {
  language: ResellerLanguagePublication;
  org: ResellerLanguageOrg;
  onEnable: (languageId: number, organizationId: number) => void;
  onRevokeRequest: (target: RevokeTarget) => void;
  isPending: boolean;
}) {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();
  const hasAccess = org.publication_id !== null;

  const handleToggle = () => {
    if (hasAccess && org.publication_id !== null) {
      onRevokeRequest({
        languageName: language.language_name,
        organizationName: org.organization_name,
        publicationId: org.publication_id
      });
    } else {
      onEnable(language.language_id, org.organization_id);
    }
  };

  return (
    <div
      className="flex items-center justify-between px-4 py-3 rounded-xl"
      style={{ backgroundColor: getColour('thirdLayer') }}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-sm font-medium truncate"
          style={{ color: getColour('normalText') }}
        >
          {org.organization_name}
          {org.is_reseller_root && (
            <span
              className="ml-2 text-xs font-normal"
              style={{ color: getColour('inactiveText') }}
            >
              ({t('you')})
            </span>
          )}
        </p>
      </div>
      <div className="ml-4 flex items-center gap-3 shrink-0">
        <span
          className="text-xs"
          style={{
            color: hasAccess
              ? getColour('activeText')
              : getColour('inactiveText')
          }}
        >
          {hasAccess ? t('enabled') : t('disabled')}
        </span>
        <Switch
          checked={hasAccess}
          onChange={handleToggle}
          disabled={isPending}
          ariaLabel={`${hasAccess ? t('disable') : t('enable')} ${language.language_name} for ${org.organization_name}`}
        />
      </div>
    </div>
  );
}

function LanguageCard({
  language,
  onEnable,
  onRevokeRequest,
  pendingOrgIds
}: {
  language: ResellerLanguagePublication;
  onEnable: (languageId: number, organizationId: number) => void;
  onRevokeRequest: (target: RevokeTarget) => void;
  pendingOrgIds: Set<number>;
}) {
  const { getColour } = useThemeContext();

  return (
    <div
      className="rounded-2xl border border-hairline shadow-card"
      style={{
        backgroundColor: getColour('secondLayer'),
        borderColor: getColour('border')
      }}
    >
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <Globe
          className="h-4 w-4 shrink-0"
          style={{ color: getColour('activeText') }}
        />
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {language.language_name}
          </h3>
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {language.language_key}
          </p>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-2">
        {language.organizations.map(org => (
          <OrgAccessRow
            key={org.organization_id}
            language={language}
            org={org}
            onEnable={onEnable}
            onRevokeRequest={onRevokeRequest}
            isPending={pendingOrgIds.has(org.organization_id)}
          />
        ))}
      </div>
    </div>
  );
}

export default function ResellerLanguageAccess() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  const [pendingOrgIds, setPendingOrgIds] = useState<Set<number>>(new Set());

  const {
    data: languages,
    isLoading,
    isError
  } = useGetResellerLanguagePublications();

  const { mutateAsync: enablePublication } =
    useEnableResellerLanguagePublication();
  const { mutateAsync: disablePublication } =
    useDisableResellerLanguagePublication();

  const markPending = (orgId: number) =>
    setPendingOrgIds(prev => new Set(prev).add(orgId));
  const clearPending = (orgId: number) =>
    setPendingOrgIds(prev => {
      const next = new Set(prev);
      next.delete(orgId);
      return next;
    });

  const handleEnable = async (languageId: number, organizationId: number) => {
    markPending(organizationId);
    try {
      await enablePublication({
        language_id: languageId,
        organization_id: organizationId
      });
      queryClient.invalidateQueries([RESELLER_LANGUAGE_PUBLICATIONS_QUERY_KEY]);
      toast.success(t('language-access-enabled'));
    } catch {
      toast.error(t('failed-to-enable-language-access'));
    } finally {
      clearPending(organizationId);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    const { publicationId, organizationName } = revokeTarget;
    setRevokeTarget(null);

    const toastId = `revoke-${publicationId}`;
    toast.loading(t('revoking-language-access'), { id: toastId });
    try {
      await disablePublication(publicationId);
      queryClient.invalidateQueries([RESELLER_LANGUAGE_PUBLICATIONS_QUERY_KEY]);
      toast.success(
        t('language-access-revoked-for', { organization: organizationName }),
        { id: toastId }
      );
    } catch {
      toast.error(t('failed-to-revoke-language-access'), { id: toastId });
    }
  };

  return (
    <div className="bg-app min-h-screen pb-4 space-y-4 sm:space-y-6">
      <div
        className="rounded-2xl border border-hairline shadow-card p-4 sm:p-6"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border')
        }}
      >
        <h1
          className="text-lg font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {t('language-access')}
        </h1>
        <p
          className="mt-1 text-sm"
          style={{ color: getColour('inactiveText') }}
        >
          {t('language-access-description')}
        </p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 rounded-2xl bg-red-500/10 border border-red-500/25 px-4 py-3 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{t('failed-to-load-data')}</span>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2
            className="h-6 w-6 animate-spin"
            style={{ color: getColour('activeText') }}
          />
        </div>
      )}

      {!isLoading && !isError && languages?.length === 0 && (
        <div
          className="rounded-2xl border border-hairline p-10 flex flex-col items-center gap-3 text-center"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border')
          }}
        >
          <Globe
            className="h-8 w-8"
            style={{ color: getColour('inactiveText') }}
          />
          <p
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('no-languages-available')}
          </p>
          <p
            className="text-xs max-w-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {t('no-languages-available-description')}
          </p>
        </div>
      )}

      {!isLoading && !isError && languages && languages.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {languages.map(language => (
            <LanguageCard
              key={language.language_id}
              language={language}
              onEnable={handleEnable}
              onRevokeRequest={setRevokeTarget}
              pendingOrgIds={pendingOrgIds}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        size="small"
        title={t('revoke-language-access')}
        footerActions={[
          <Button
            key="cancel"
            label={t('cancel')}
            size="small"
            variant="smoke"
            onClick={() => setRevokeTarget(null)}
          />,
          <Button
            key="confirm"
            label={t('revoke-access')}
            size="small"
            variant="danger"
            onClick={handleRevokeConfirm}
          />
        ]}
      >
        <p className="text-sm" style={{ color: getColour('normalText') }}>
          {t('revoke-language-access-confirmation', {
            language: revokeTarget?.languageName,
            organization: revokeTarget?.organizationName
          })}
        </p>
      </Modal>
    </div>
  );
}
